import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Clock, SignIn, SignOut, Circle, Globe } from "@phosphor-icons/react";

/* ============================================================================
   SessionsPanel — last N login_events for a COE member. Lives on the
   Member Profile page, surfacing the answer to "when did this person
   actually use the system?" Now that the verify-otp endpoint writes a
   login_events row per session, this panel is the operator-facing view.
   ========================================================================== */

const fmtDT = (s) =>
    s
        ? new Date(s).toLocaleString("en-IN", {
              day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
          })
        : "—";

const fmtDur = (mins) => {
    if (mins == null) return "—";
    if (mins < 1) return "<1m";
    if (mins < 60) return `${Math.round(mins)}m`;
    const h = Math.floor(mins / 60);
    const m = Math.round(mins - h * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const shortUA = (ua) => {
    if (!ua) return "—";
    // Quick + dirty browser/OS extraction so the table column is scannable
    let browser = "Browser";
    if (/Edg\//.test(ua)) browser = "Edge";
    else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = "Chrome";
    else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";
    else if (/Firefox\//.test(ua)) browser = "Firefox";
    let os = "";
    if (/Windows/.test(ua)) os = "Windows";
    else if (/Mac OS X/.test(ua)) os = "macOS";
    else if (/Android/.test(ua)) os = "Android";
    else if (/iPhone|iPad|iOS/.test(ua)) os = "iOS";
    else if (/Linux/.test(ua)) os = "Linux";
    return os ? `${browser} · ${os}` : browser;
};

export function SessionsPanel({ memberId, endpoint }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        const url = endpoint || `/admin/coe/people/${memberId}/sessions`;
        api.get(url).then((r) => setData(r.data));
    }, [memberId, endpoint]);

    if (!data) {
        return <div className="text-muted-foreground text-sm">Loading sessions…</div>;
    }

    const sessions = data.sessions || [];
    if (sessions.length === 0) {
        return (
            <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="coe-sessions-empty">
                <Clock size={28} className="mx-auto text-muted-foreground mb-2" />
                <div className="font-display text-lg font-bold">No login activity recorded yet.</div>
                <p className="text-xs text-muted-foreground mt-1">
                    Login events have been tracked since the platform shipped session capture. This member hasn't logged in since.
                </p>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="coe-sessions">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Last 50 sessions</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Last login {fmtDT(data.last_login_at)}{" "}
                        {data.is_online ? (
                            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold text-[10px] uppercase tracking-wide">
                                <Circle size={6} weight="fill" /> Online
                            </span>
                        ) : (
                            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-muted-foreground font-bold text-[10px] uppercase tracking-wide">
                                <Circle size={6} weight="fill" /> Offline
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40">
                        <tr>
                            <th className="px-3 py-2 text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground text-left">Login</th>
                            <th className="px-3 py-2 text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground text-left">Logout</th>
                            <th className="px-3 py-2 text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground text-left">Duration</th>
                            <th className="px-3 py-2 text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground text-left">Device</th>
                            <th className="px-3 py-2 text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground text-left">IP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sessions.map((s) => (
                            <tr key={s.id} className="border-t border-border" data-testid={`coe-session-row-${s.id}`}>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-1.5 text-xs">
                                        <SignIn size={11} className="text-emerald-700" />
                                        {fmtDT(s.login_at)}
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-xs text-muted-foreground">
                                    {s.logout_at ? (
                                        <div className="flex items-center gap-1.5">
                                            <SignOut size={11} className="text-rose-600" />
                                            {fmtDT(s.logout_at)}
                                        </div>
                                    ) : (
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Still open</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-xs tabular-nums">{fmtDur(s.duration_minutes)}</td>
                                <td className="px-3 py-2 text-xs text-muted-foreground">{shortUA(s.user_agent)}</td>
                                <td className="px-3 py-2 text-xs text-muted-foreground font-mono">
                                    <span className="inline-flex items-center gap-1">
                                        <Globe size={10} /> {s.ip || "—"}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
